import { useMemo } from 'react';
import { VehicleFeature } from '@/catalog/types/vehicle';
import { CATEGORY_MAPPINGS } from '@/common/utils/category-mappings';
import { normalizeDisplayName } from '@/common/utils/spec-display';

interface GroupedFeatures {
  [category: string]: VehicleFeature[];
}

type CategoryType = keyof typeof CATEGORY_MAPPINGS;

function mergeMileageFeatures(features: VehicleFeature[]): VehicleFeature[] {
  const city = features.find((s) => s.key === 'city_mileage');
  const hwy = features.find((s) => s.key === 'highway_mileage');
  const others = features.filter(
    (s) => s.key !== 'city_mileage' && s.key !== 'highway_mileage'
  );

  if (city || hwy) {
    others.push({
      key: 'mpg',
      display_name: 'MPG',
      display_value: `${city?.display_value ?? '-'} cty / ${hwy?.display_value ?? '-'} hwy`,
      value: `${city?.value ?? '-'} / ${hwy?.value ?? '-'}`,
      type: 'string',
      category: '',
      attribute_category: city?.attribute_category || hwy?.attribute_category || '',
      ranking_value: Math.max(city?.ranking_value ?? 0, hwy?.ranking_value ?? 0),
    });
  }

  return others;
}

export function useGroupedFeatures(
  features_spec: VehicleFeature[],
  allowedCategories: string[],
  sortAscending: boolean = false,
  type: CategoryType = 'specifications'
): GroupedFeatures {
  return useMemo(() => {
    const baseFeatures: VehicleFeature[] =
      type === 'specifications'
        ? mergeMileageFeatures(features_spec).map(normalizeDisplayName)
        : features_spec.filter((f) => {
            const value = f.value != null ? String(f.value).toLowerCase() : undefined;

            if (f.key === 'usb') {
              return value !== '0' && !!f.display_value;
            } else {
              const hasDisplayValue = !!f.display_value;
              const isFeatureMissing = value === 'false';

              return hasDisplayValue && !isFeatureMissing;
            }
          });

    const grouped = baseFeatures.reduce<GroupedFeatures>((acc, feature) => {
      const rawCategory = feature.attribute_category || feature.category;
      if (!rawCategory || !allowedCategories.includes(rawCategory)) return acc;

      const mappedCategory =
        CATEGORY_MAPPINGS[type][
          rawCategory as keyof (typeof CATEGORY_MAPPINGS)[CategoryType]
        ] || rawCategory;

      if (!acc[mappedCategory]) {
        acc[mappedCategory] = [];
      }

      acc[mappedCategory].push(feature);
      return acc;
    }, {});

    // Sort
    Object.values(grouped).forEach((features) =>
      features.sort((a, b) =>
        sortAscending
          ? (a.ranking_value ?? 0) - (b.ranking_value ?? 0)
          : (b.ranking_value ?? 0) - (a.ranking_value ?? 0)
      )
    );

    // Return in proper order
    return Object.values(CATEGORY_MAPPINGS[type]).reduce<GroupedFeatures>(
      (acc, mappedCategory) => {
        if (grouped[mappedCategory]) {
          acc[mappedCategory] = grouped[mappedCategory];
        }
        return acc;
      },
      {}
    );
  }, [features_spec, allowedCategories, sortAscending, type]);
}
